import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { apiRequest, queryClient, getExtraHeaders } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  BrainCircuit,
  RefreshCw,
  Gauge,
  Loader2,
  Copy,
  Download,
  Sparkles,
  Trash2,
  Plus,
  Upload,
  AlertTriangle,
  Network,
  Timer,
  School,
  ClipboardList,
  Layers,
  Users,
  Target,
  ArrowUpDown,
} from "lucide-react";

// ---------------------------------------------------------------------------------
// Types (mirror server/services/moat.ts + the /api/admin/moat/* routes)
// ---------------------------------------------------------------------------------

interface MoatSummary {
  items: { calibrated: number; byFlag: Record<string, number> };
  distractorRows: number;
  prerequisiteEdges: number;
  retentionTopics: number;
  schools: number;
  outcomes: number;
  abilities: number;
  calibration: null | { n: number; intercept: number; slope: number; fittedAt: string };
}

interface FlaggedItem {
  questionId: string;
  flag: string;
  a: number;
  b: number;
  pValue: number;
  pointBiserial: number;
  responses: number;
  questionText: string;
  subject: string;
  topic: string;
  grade: number;
  status: string | null;
}

interface Misconception {
  questionId: string;
  questionText: string;
  subject: string;
  topic: string;
  grade: number;
  correctOptionText: string | null;
  distractorId: string;
  distractorText: string | null;
  picks: number;
  responses: number;
  shareOfWrong: number;
  shareOfAll: number;
}

interface PrerequisiteEdge {
  grade: number;
  subject: string;
  prereqTopicDisplay: string;
  topicDisplay: string;
  lift: number;
  precedence: number;
  support: number;
}

interface RetentionRow {
  grade: number;
  subject: string;
  topicDisplay: string;
  halfLifeDays: number;
  maxDrop: number;
  samples: number;
  accByGap: Record<string, number> | null;
}

interface SchoolRow {
  schoolName: string;
  parish: string | null;
  grade: number;
  students: number;
  answers: number;
  accuracy: number;
  source: string;
}

interface ReadinessReport {
  generatedAt: string;
  schools: Array<{
    grade: number;
    school: string;
    parish: string | null;
    students: number;
    withAbility: number;
    avgPercentile: number | null;
    legacyStudents: number | null;
    legacyAccuracy: number | null;
  }>;
  parishes: Array<{
    parish: string;
    grade: number;
    liveStudents: number;
    avgPercentile: number | null;
    legacyStudents: number;
    legacyAccuracy: number | null;
  }>;
}

type SubjectKey = "mathematics" | "languageArts" | "science" | "socialStudies" | "abilityTest" | "overall";
type SubjectResult = { level?: string | null; score?: number | null };

interface Outcome {
  id: string;
  userId: string | null;
  legacyId: number | null;
  examYear: number;
  examGrade: number;
  placementSchool: string | null;
  subjects: Partial<Record<SubjectKey, SubjectResult>>;
  reportedBy: string;
  consent: boolean;
  notes: string | null;
  createdAt: string;
  student: { id: string; firstName: string; lastName: string; school: string | null; grade: number } | null;
}

interface OutcomesResponse {
  outcomes: Outcome[];
  calibration: MoatSummary["calibration"];
}

interface CampaignStudent {
  userId: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  parentEmail: string | null;
  parentPhone: string | null;
  school: string | null;
  grade: number;
  lastActiveAt: string | null;
}

// ---------------------------------------------------------------------------------
// Constants + small helpers
// ---------------------------------------------------------------------------------

const SUBJECT_OPTIONS = ["Mathematics", "Language Arts", "Science", "Social Studies"];

const SUBJECT_KEYS: Array<{ key: SubjectKey; label: string }> = [
  { key: "mathematics", label: "Mathematics" },
  { key: "languageArts", label: "Language Arts" },
  { key: "science", label: "Science" },
  { key: "socialStudies", label: "Social Studies" },
  { key: "abilityTest", label: "Ability Test" },
  { key: "overall", label: "Overall" },
];

const PEP_LEVELS = ["Beginning", "Developing", "Proficient", "Highly Proficient"];

const LEVEL_BADGE: Record<string, string> = {
  "Highly Proficient": "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  Proficient: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  Developing: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  Beginning: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
};

const FLAG_META: Record<string, { label: string; help: string; className: string }> = {
  "bad-key-or-ambiguous": {
    label: "Bad key or ambiguous",
    help: "Strong students get this wrong more often than weak ones. The answer key is probably wrong, or the wording is ambiguous.",
    className: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  },
  "weak-discrimination": {
    label: "Weak discrimination",
    help: "The question barely separates students who know the topic from students who do not. It carries little information.",
    className: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  },
  "too-easy": {
    label: "Too easy",
    help: "Almost everyone gets it right, so it tells us very little about a student.",
    className: "bg-muted text-muted-foreground",
  },
  "too-hard": {
    label: "Too hard",
    help: "Almost nobody gets it right, so it tells us very little about a student.",
    className: "bg-muted text-muted-foreground",
  },
};

const GAP_LABELS = ["<1d", "1-3d", "3-7d", "7-14d", "14-30d", "30-60d", "60-120d", "120d+"];

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: "include", headers: getExtraHeaders() });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    let message = text || `Request failed (${res.status})`;
    try {
      const parsed = JSON.parse(text);
      message = parsed.error || parsed.message || message;
    } catch {
      /* not JSON, keep the raw text */
    }
    throw new Error(message);
  }
  return res.json();
}

function pct(value: number | null | undefined, fractionOfOne = true): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "n/a";
  return `${Math.round(fractionOfOne ? value * 100 : value)}%`;
}

function truncate(text: string, max = 120): string {
  const clean = (text || "").replace(/\s+/g, " ").trim();
  return clean.length > max ? `${clean.slice(0, max)}…` : clean;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "n/a";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "n/a" : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function csvCell(value: unknown): string {
  const s = value === null || value === undefined ? "" : String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function toCsv(rows: unknown[][]): string {
  return rows.map(r => r.map(csvCell).join(",")).join("\n");
}

function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** RFC-4180-ish CSV parser: handles quoted fields, doubled quotes and CRLF. */
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } else { inQuotes = false; }
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      row.push(field); field = "";
    } else if (c === "\n") {
      row.push(field); field = ""; rows.push(row); row = [];
    } else if (c !== "\r") {
      field += c;
    }
  }
  if (field !== "" || row.length) { row.push(field); rows.push(row); }
  return rows.filter(r => r.some(c => c.trim() !== ""));
}

function copyToClipboard(text: string, done: () => void) {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(done).catch(done);
  } else {
    done();
  }
}

function TableSkeleton({ rows = 6, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <div className="space-y-2" data-testid="moat-skeleton">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-3">
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={c} className={`h-8 ${c === 0 ? "flex-[3]" : "flex-1"}`} />
          ))}
        </div>
      ))}
    </div>
  );
}

function EmptyState({ title, hint }: { title: string; hint: string }) {
  return (
    <div className="text-center py-10 space-y-1">
      <p className="font-medium">{title}</p>
      <p className="text-sm text-muted-foreground">{hint}</p>
    </div>
  );
}

function ErrorState({ error }: { error: unknown }) {
  return (
    <div className="text-center py-10 space-y-1">
      <p className="font-medium text-destructive">Could not load this data</p>
      <p className="text-sm text-muted-foreground">{(error as Error)?.message || "Unknown error"}</p>
    </div>
  );
}

function GradeSelect({ value, onChange, className = "w-[150px]" }: { value: string; onChange: (v: string) => void; className?: string }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={className}><SelectValue placeholder="Grade" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All grades</SelectItem>
        <SelectItem value="4">Grade 4</SelectItem>
        <SelectItem value="5">Grade 5</SelectItem>
        <SelectItem value="6">Grade 6</SelectItem>
      </SelectContent>
    </Select>
  );
}

function CopyIdButton({ id }: { id: string }) {
  const { toast } = useToast();
  return (
    <Button
      size="sm"
      variant="ghost"
      className="font-mono text-xs"
      title="Copy question id"
      onClick={() => copyToClipboard(id, () => toast({ title: "Question id copied", description: id }))}
      data-testid={`button-copy-${id}`}
    >
      <Copy className="w-3.5 h-3.5 mr-1.5" />
      {id.slice(0, 8)}
    </Button>
  );
}

// ---------------------------------------------------------------------------------
// 1. Overview
// ---------------------------------------------------------------------------------

function StatCard({ label, value, explanation, icon: Icon }: { label: string; value: number | string; explanation: string; icon: any }) {
  return (
    <Card data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <CardContent className="pt-5 space-y-1.5">
        <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wide">
          <Icon className="w-3.5 h-3.5" /> {label}
        </div>
        <p className="text-2xl font-bold">{typeof value === "number" ? value.toLocaleString() : value}</p>
        <p className="text-xs text-muted-foreground leading-snug">{explanation}</p>
      </CardContent>
    </Card>
  );
}

function OverviewTab() {
  const { toast } = useToast();
  const { data, isLoading, error } = useQuery<MoatSummary>({ queryKey: ["/api/admin/moat/summary"] });

  const refreshMutation = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/admin/moat/refresh")).json(),
    onSuccess: (res: { abilities: number; items: number; distractors: number }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/moat/summary"] });
      toast({
        title: "Live data refreshed",
        description: `${res.abilities ?? 0} ability estimates, ${res.items ?? 0} item calibrations, ${res.distractors ?? 0} distractor rows updated.`,
      });
    },
    onError: (e: any) => toast({ title: "Refresh failed", description: e?.message || "Unknown error", variant: "destructive" }),
  });

  const calibrateMutation = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/admin/moat/calibrate")).json(),
    onSuccess: (res: { message?: string; calibration: MoatSummary["calibration"] }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/moat/summary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/moat/outcomes"] });
      toast({
        title: res.calibration ? "Predictor recalibrated" : "Not enough outcomes yet",
        description: res.message || "",
        variant: res.calibration ? "default" : "destructive",
      });
    },
    onError: (e: any) => toast({ title: "Calibration failed", description: e?.message || "Unknown error", variant: "destructive" }),
  });

  const flagged = useMemo(() => {
    if (!data) return 0;
    return Object.entries(data.items.byFlag || {})
      .filter(([flag]) => flag !== "ok")
      .reduce((sum, [, n]) => sum + Number(n || 0), 0);
  }, [data]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <CardTitle className="text-base">Maintenance</CardTitle>
              <CardDescription>
                Refresh folds the last week of live Academy answers into the legacy calibration. Recalibrate refits the PEP predictor on the real exam results on file.
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => refreshMutation.mutate()} disabled={refreshMutation.isPending} data-testid="button-refresh-moat">
                {refreshMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Refresh live data
              </Button>
              <Button onClick={() => calibrateMutation.mutate()} disabled={calibrateMutation.isPending} data-testid="button-calibrate-predictor">
                {calibrateMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Gauge className="w-4 h-4 mr-2" />}
                Recalibrate predictor
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {error ? (
        <Card><CardContent><ErrorState error={error} /></CardContent></Card>
      ) : isLoading || !data ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Calibrated items"
              value={data.items.calibrated}
              icon={Layers}
              explanation="Questions with a measured difficulty and discrimination, learned from how Jamaican students actually answered them."
            />
            <StatCard
              label="Flagged items"
              value={flagged}
              icon={AlertTriangle}
              explanation="Calibrated questions that look broken: a likely wrong answer key, ambiguous wording, or too easy or hard to be informative."
            />
            <StatCard
              label="Misconception rows"
              value={data.distractorRows}
              icon={Target}
              explanation="Wrong-answer patterns: for each question, which specific wrong option students are drawn to and how often."
            />
            <StatCard
              label="Prerequisite edges"
              value={data.prerequisiteEdges}
              icon={Network}
              explanation="Learned 'you need A before B' links between topics, used to order each student's study plan."
            />
            <StatCard
              label="Retention topics"
              value={data.retentionTopics}
              icon={Timer}
              explanation="Topics with a measured forgetting curve, used to schedule reviews before the knowledge fades."
            />
            <StatCard
              label="Schools benchmarked"
              value={data.schools}
              icon={School}
              explanation="Distinct Jamaican schools with an aggregate accuracy benchmark from the legacy archive."
            />
            <StatCard
              label="Students with an ability estimate"
              value={data.abilities}
              icon={Users}
              explanation="Accounts placed on the national ability scale, so we can say where a child sits against every Jamaican student on record."
            />
            <StatCard
              label="Real PEP outcomes"
              value={data.outcomes}
              icon={ClipboardList}
              explanation="Actual exam results reported back to us. These are the ground truth the readiness predictor is scored against."
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><Gauge className="w-4 h-4" /> Predictor calibration</CardTitle>
                <CardDescription>How well the readiness score maps onto a real chance of reaching Proficient.</CardDescription>
              </CardHeader>
              <CardContent>
                {data.calibration ? (
                  <div className="space-y-2">
                    <Badge className="border-0 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">Calibrated</Badge>
                    <p className="text-sm">
                      Fitted on <span className="font-semibold">{data.calibration.n.toLocaleString()}</span> labelled student{data.calibration.n === 1 ? "" : "s"}.
                    </p>
                    <p className="text-sm text-muted-foreground">Last fitted {formatDate(data.calibration.fittedAt)}.</p>
                    <p className="text-xs text-muted-foreground font-mono">
                      intercept {data.calibration.intercept.toFixed(3)} · slope {data.calibration.slope.toFixed(4)}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Badge variant="outline">Not calibrated</Badge>
                    <p className="text-sm text-muted-foreground">Not yet calibrated. Needs 30 or more real PEP results.</p>
                    <p className="text-sm text-muted-foreground">
                      Add outcomes on the PEP outcomes tab, then press Recalibrate predictor.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Item flags</CardTitle>
                <CardDescription>Breakdown of the calibrated question bank by quality flag.</CardDescription>
              </CardHeader>
              <CardContent>
                {Object.keys(data.items.byFlag || {}).length === 0 ? (
                  <EmptyState title="No calibrated items yet" hint="Run the legacy import, then press Refresh live data." />
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(data.items.byFlag).sort((a, b) => b[1] - a[1]).map(([flag, n]) => (
                      <Badge key={flag} className={`border-0 ${flag === "ok" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : FLAG_META[flag]?.className || "bg-muted text-muted-foreground"}`}>
                        {flag === "ok" ? "Healthy" : FLAG_META[flag]?.label || flag}: {Number(n).toLocaleString()}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------------
// 2. Item quality
// ---------------------------------------------------------------------------------

function ItemQualityTab() {
  const [flag, setFlag] = useState("all");

  const { data: items = [], isLoading, error } = useQuery<FlaggedItem[]>({
    queryKey: ["/api/admin/moat/items", flag],
    queryFn: () => fetchJson<FlaggedItem[]>(`/api/admin/moat/items?${new URLSearchParams(flag === "all" ? {} : { flag })}`),
  });

  const chips = [{ value: "all", label: "All flags" }, ...Object.entries(FLAG_META).map(([value, m]) => ({ value, label: m.label }))];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">What these numbers mean</CardTitle>
          <CardDescription>Every question is scored against the answers real Jamaican students gave it.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-sm">
          <div><span className="font-semibold">b: difficulty.</span> <span className="text-muted-foreground">0 is the average Jamaican student. Negative is easier than average, positive is harder.</span></div>
          <div><span className="font-semibold">a: discrimination.</span> <span className="text-muted-foreground">How sharply the question separates students who know the topic from those who do not. Higher is better; below about 0.5 is weak.</span></div>
          <div><span className="font-semibold">p: percent correct.</span> <span className="text-muted-foreground">The plain share of students who got it right. Very high or very low means the question teaches us little.</span></div>
          <div><span className="font-semibold">pbis: point-biserial.</span> <span className="text-muted-foreground">Correlation between getting this right and doing well overall. Near zero or negative usually means a wrong answer key.</span></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center gap-2">
            {chips.map(c => (
              <Button
                key={c.value}
                size="sm"
                variant={flag === c.value ? "default" : "outline"}
                onClick={() => setFlag(c.value)}
                data-testid={`chip-flag-${c.value}`}
              >
                {c.label}
              </Button>
            ))}
            <div className="ml-auto text-sm text-muted-foreground">{items.length} item{items.length === 1 ? "" : "s"}</div>
          </div>
        </CardHeader>
        <CardContent>
          {error ? (
            <ErrorState error={error} />
          ) : isLoading ? (
            <TableSkeleton rows={8} cols={6} />
          ) : items.length === 0 ? (
            <EmptyState
              title="No flagged items"
              hint="Either the bank is healthy for this filter, or nothing is calibrated yet. Run the legacy import and press Refresh live data on the Overview tab."
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[280px]">Question</TableHead>
                    <TableHead>Grade / Subject / Topic</TableHead>
                    <TableHead>Flag</TableHead>
                    <TableHead className="text-right">b</TableHead>
                    <TableHead className="text-right">a</TableHead>
                    <TableHead className="text-right">p</TableHead>
                    <TableHead className="text-right">pbis</TableHead>
                    <TableHead className="text-right">Responses</TableHead>
                    <TableHead className="text-right">Question</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map(item => {
                    const meta = FLAG_META[item.flag];
                    return (
                      <TableRow key={item.questionId} data-testid={`row-item-${item.questionId}`}>
                        <TableCell className="max-w-[380px]">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-sm">{truncate(item.questionText, 120)}</span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-md whitespace-pre-wrap">{item.questionText}</TooltipContent>
                          </Tooltip>
                          {item.status && <div className="text-xs text-muted-foreground mt-0.5">status: {item.status}</div>}
                        </TableCell>
                        <TableCell className="text-sm whitespace-nowrap">
                          Grade {item.grade}
                          <br />
                          <span className="text-muted-foreground">{item.subject} · {item.topic}</span>
                        </TableCell>
                        <TableCell>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge className={`border-0 ${meta?.className || "bg-muted text-muted-foreground"}`}>{meta?.label || item.flag}</Badge>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">{meta?.help || item.flag}</TooltipContent>
                          </Tooltip>
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <span className="font-mono">{item.b.toFixed(2)}</span>
                          <div className="text-[10px] text-muted-foreground">
                            {item.b < -0.15 ? "easier than average" : item.b > 0.15 ? "harder than average" : "about average"}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono">{item.a.toFixed(2)}</TableCell>
                        <TableCell className="text-right font-mono">{pct(item.pValue)}</TableCell>
                        <TableCell className="text-right font-mono">{item.pointBiserial.toFixed(2)}</TableCell>
                        <TableCell className="text-right font-mono">{item.responses.toLocaleString()}</TableCell>
                        <TableCell className="text-right"><CopyIdButton id={item.questionId} /></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------------
// 3. Misconceptions
// ---------------------------------------------------------------------------------

function MisconceptionsTab() {
  const { toast } = useToast();
  const [grade, setGrade] = useState("all");
  const [subject, setSubject] = useState("all");
  const [topicInput, setTopicInput] = useState("");
  const [topic, setTopic] = useState("");
  const [minShare, setMinShare] = useState("0.45");

  const { data: rows = [], isLoading, error } = useQuery<Misconception[]>({
    queryKey: ["/api/admin/moat/misconceptions", grade, subject, topic, minShare],
    queryFn: () => {
      const params = new URLSearchParams();
      if (grade !== "all") params.set("grade", grade);
      if (subject !== "all") params.set("subject", subject);
      if (topic.trim()) params.set("topic", topic.trim());
      params.set("minShare", minShare);
      return fetchJson<Misconception[]>(`/api/admin/moat/misconceptions?${params}`);
    },
  });

  const lessonMutation = useMutation({
    mutationFn: async (vars: { questionId: string; distractorId: string }) =>
      (await apiRequest("POST", "/api/admin/moat/misconceptions/lesson", vars)).json(),
    onSuccess: (res: { lesson: { id: string; title: string; slug: string } }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/lessons"] });
      toast({
        title: "Draft lesson created",
        description: `Draft lesson created: ${res.lesson?.title ?? "untitled"}`,
        action: (
          <ToastAction altText="Open Interactive Lessons" asChild>
            <Link href="/admin/lessons">Open lessons</Link>
          </ToastAction>
        ),
      });
    },
    onError: (e: any) =>
      toast({ title: "Could not draft the lesson", description: e?.message || "Unknown error", variant: "destructive" }),
  });

  const pendingKey = lessonMutation.isPending
    ? `${lessonMutation.variables?.questionId}:${lessonMutation.variables?.distractorId}`
    : null;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Where Jamaican students go wrong</CardTitle>
          <CardDescription>
            A misconception is a single wrong option that swallows most of the wrong answers on a question. That is not random guessing, it is a
            specific idea a lot of children hold. Each row can be turned into a draft remediation lesson that teaches against it.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <GradeSelect value={grade} onChange={setGrade} />
            <Select value={subject} onValueChange={setSubject}>
              <SelectTrigger className="w-[190px]"><SelectValue placeholder="Subject" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All subjects</SelectItem>
                {SUBJECT_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Input
                className="w-[220px]"
                placeholder="Topic contains…"
                value={topicInput}
                onChange={e => setTopicInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") setTopic(topicInput); }}
                onBlur={() => setTopic(topicInput)}
                data-testid="input-misconception-topic"
              />
              <Button variant="outline" onClick={() => setTopic(topicInput)}>Apply</Button>
            </div>
            <Select value={minShare} onValueChange={setMinShare}>
              <SelectTrigger className="w-[230px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["0.35", "0.45", "0.55", "0.65", "0.75"].map(v => (
                  <SelectItem key={v} value={v}>At least {Math.round(parseFloat(v) * 100)}% of wrong answers</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="ml-auto text-sm text-muted-foreground self-center">{rows.length} misconception{rows.length === 1 ? "" : "s"}</div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          {error ? (
            <ErrorState error={error} />
          ) : isLoading ? (
            <TableSkeleton rows={8} cols={5} />
          ) : rows.length === 0 ? (
            <EmptyState
              title="No misconceptions yet"
              hint="Run the legacy import, or loosen the filters. A question needs at least 200 recorded responses to qualify."
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[300px]">Question</TableHead>
                    <TableHead>Correct answer</TableHead>
                    <TableHead>Trap answer</TableHead>
                    <TableHead className="text-right">Chose the trap</TableHead>
                    <TableHead className="text-right">Of wrong answers</TableHead>
                    <TableHead className="text-right">Responses</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(m => {
                    const key = `${m.questionId}:${m.distractorId}`;
                    return (
                      <TableRow key={key} data-testid={`row-misconception-${key}`}>
                        <TableCell className="max-w-[420px]">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-sm">{truncate(m.questionText, 120)}</span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-md whitespace-pre-wrap">{m.questionText}</TooltipContent>
                          </Tooltip>
                          <div className="text-xs text-muted-foreground mt-0.5">Grade {m.grade} · {m.subject} · {m.topic}</div>
                        </TableCell>
                        <TableCell className="text-sm text-emerald-700 dark:text-emerald-400 max-w-[180px]">
                          {m.correctOptionText || <span className="text-muted-foreground">n/a</span>}
                        </TableCell>
                        <TableCell className="text-sm text-red-700 dark:text-red-400 max-w-[180px]">
                          {m.distractorText || <span className="font-mono text-muted-foreground">option {m.distractorId}</span>}
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <span className="font-semibold">{pct(m.shareOfAll)}</span>
                          <div className="text-[10px] text-muted-foreground">of all students</div>
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <span className="font-semibold">{pct(m.shareOfWrong)}</span>
                          <div className="text-[10px] text-muted-foreground">{m.picks.toLocaleString()} picks</div>
                        </TableCell>
                        <TableCell className="text-right font-mono">{m.responses.toLocaleString()}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={lessonMutation.isPending}
                            onClick={() => lessonMutation.mutate({ questionId: m.questionId, distractorId: m.distractorId })}
                            data-testid={`button-remediate-${key}`}
                          >
                            {pendingKey === key ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                            Draft remediation lesson
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------------
// 4. Prerequisites
// ---------------------------------------------------------------------------------

function PrerequisitesTab() {
  const [grade, setGrade] = useState("all");

  const { data: edges = [], isLoading, error } = useQuery<PrerequisiteEdge[]>({
    queryKey: ["/api/admin/moat/prerequisites", grade],
    queryFn: () => fetchJson<PrerequisiteEdge[]>(`/api/admin/moat/prerequisites?${new URLSearchParams(grade === "all" ? {} : { grade })}`),
  });

  const bySubject = useMemo(() => {
    const map = new Map<string, PrerequisiteEdge[]>();
    for (const e of edges) {
      const list = map.get(e.subject) || [];
      list.push(e);
      map.set(e.subject, list);
    }
    for (const list of Array.from(map.values())) list.sort((a, b) => b.lift - a.lift);
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [edges]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">What unlocks what</CardTitle>
          <CardDescription>
            Learned from the archive: students who were already strong on the first topic did measurably better on their first attempts at the second one.
            The study plan uses these edges to order a child's work and to show the "Unlocks X" hint on a topic.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <GradeSelect value={grade} onChange={setGrade} />
            <div className="ml-auto text-sm text-muted-foreground self-center">{edges.length} edge{edges.length === 1 ? "" : "s"}</div>
          </div>
        </CardContent>
      </Card>

      {error ? (
        <Card><CardContent className="pt-6"><ErrorState error={error} /></CardContent></Card>
      ) : isLoading ? (
        <Card><CardContent className="pt-6"><TableSkeleton rows={6} cols={4} /></CardContent></Card>
      ) : edges.length === 0 ? (
        <Card><CardContent className="pt-6"><EmptyState title="No prerequisite edges yet" hint="Run the legacy import. Prerequisites are mined from the archive of past attempts." /></CardContent></Card>
      ) : (
        bySubject.map(([subject, list]) => (
          <Card key={subject}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Network className="w-4 h-4" /> {subject}</CardTitle>
              <CardDescription>{list.length} edge{list.length === 1 ? "" : "s"}, strongest lift first.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[320px]">Prerequisite → Topic</TableHead>
                      <TableHead>Grade</TableHead>
                      <TableHead className="text-right">Lift</TableHead>
                      <TableHead className="text-right">Order</TableHead>
                      <TableHead className="text-right">Support</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {list.map((e, i) => (
                      <TableRow key={`${e.grade}-${e.prereqTopicDisplay}-${e.topicDisplay}-${i}`} data-testid={`row-prereq-${i}`}>
                        <TableCell className="text-sm">
                          <span className="font-medium">{e.prereqTopicDisplay}</span>
                          <span className="text-muted-foreground mx-2">→</span>
                          <span className="font-medium">{e.topicDisplay}</span>
                        </TableCell>
                        <TableCell className="text-sm whitespace-nowrap">Grade {e.grade}</TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <span className="font-semibold text-emerald-700 dark:text-emerald-400">+{Math.round(e.lift * 100)} pts</span>
                          <div className="text-[10px] text-muted-foreground">early accuracy when the prerequisite is strong</div>
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <span className="font-semibold">{pct(e.precedence)}</span>
                          <div className="text-[10px] text-muted-foreground">of students did it first</div>
                        </TableCell>
                        <TableCell className="text-right font-mono">{e.support.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------------
// 5. Retention
// ---------------------------------------------------------------------------------

function GapSparkline({ accByGap }: { accByGap: Record<string, number> | null }) {
  const values = GAP_LABELS.map((_, i) => {
    const v = accByGap?.[String(i)];
    return typeof v === "number" ? v : null;
  });
  if (values.every(v => v === null)) return <span className="text-xs text-muted-foreground">no gap data</span>;
  return (
    <div className="flex items-end gap-1 h-10" data-testid="gap-sparkline">
      {values.map((v, i) => (
        <Tooltip key={i}>
          <TooltipTrigger asChild>
            <div className="flex flex-col items-center justify-end h-10 w-4">
              <div
                className={`w-full rounded-sm ${v === null ? "bg-muted" : "bg-primary/70"}`}
                style={{ height: `${v === null ? 4 : Math.max(4, Math.round(v * 40))}px` }}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent>{GAP_LABELS[i]}: {v === null ? "no data" : pct(v)}</TooltipContent>
        </Tooltip>
      ))}
    </div>
  );
}

function RetentionTab() {
  const [grade, setGrade] = useState("all");

  const { data: rows = [], isLoading, error } = useQuery<RetentionRow[]>({
    queryKey: ["/api/admin/moat/retention", grade],
    queryFn: () => fetchJson<RetentionRow[]>(`/api/admin/moat/retention?${new URLSearchParams(grade === "all" ? {} : { grade })}`),
  });

  const bySubject = useMemo(() => {
    const map = new Map<string, RetentionRow[]>();
    for (const r of rows) {
      const list = map.get(r.subject) || [];
      list.push(r);
      map.set(r.subject, list);
    }
    for (const list of Array.from(map.values())) list.sort((a, b) => a.halfLifeDays - b.halfLifeDays);
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [rows]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Forgetting curves</CardTitle>
          <CardDescription>
            Half-life is the number of days until half of the accuracy a student gained on a topic has faded. Shorter means it drains away faster.
            Review scheduling ("Review due") uses it to bring a topic back before it is gone, and mastery uses it to discount old evidence.
            The bars show accuracy at each gap since the student last practised: {GAP_LABELS.join(", ")}.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <GradeSelect value={grade} onChange={setGrade} />
            <div className="ml-auto text-sm text-muted-foreground self-center">{rows.length} topic{rows.length === 1 ? "" : "s"}</div>
          </div>
        </CardContent>
      </Card>

      {error ? (
        <Card><CardContent className="pt-6"><ErrorState error={error} /></CardContent></Card>
      ) : isLoading ? (
        <Card><CardContent className="pt-6"><TableSkeleton rows={6} cols={5} /></CardContent></Card>
      ) : rows.length === 0 ? (
        <Card><CardContent className="pt-6"><EmptyState title="No forgetting curves yet" hint="Run the legacy import. Retention is measured from repeat attempts spread over time." /></CardContent></Card>
      ) : (
        bySubject.map(([subject, list]) => (
          <Card key={subject}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Timer className="w-4 h-4" /> {subject}</CardTitle>
              <CardDescription>Fastest-forgotten topics first.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[220px]">Topic</TableHead>
                      <TableHead>Grade</TableHead>
                      <TableHead className="text-right">Half-life</TableHead>
                      <TableHead className="text-right">Max drop</TableHead>
                      <TableHead className="text-right">Samples</TableHead>
                      <TableHead className="min-w-[180px]">Accuracy by gap</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {list.map((r, i) => (
                      <TableRow key={`${r.grade}-${r.topicDisplay}-${i}`} data-testid={`row-retention-${i}`}>
                        <TableCell className="text-sm font-medium">{r.topicDisplay}</TableCell>
                        <TableCell className="text-sm whitespace-nowrap">Grade {r.grade}</TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <span className="font-semibold">{r.halfLifeDays.toFixed(1)}</span>
                          <span className="text-muted-foreground text-xs"> days</span>
                        </TableCell>
                        <TableCell className="text-right font-mono">{pct(r.maxDrop)}</TableCell>
                        <TableCell className="text-right font-mono">{r.samples.toLocaleString()}</TableCell>
                        <TableCell><GapSparkline accByGap={r.accByGap} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------------
// 6. Schools & parishes
// ---------------------------------------------------------------------------------

function SchoolsTab() {
  const [reportGrade, setReportGrade] = useState("all");
  const [legacyGrade, setLegacyGrade] = useState("all");
  const [parish, setParish] = useState("all");
  const [sortKey, setSortKey] = useState<"accuracy" | "students">("students");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const { data: report, isLoading: reportLoading, error: reportError } = useQuery<ReadinessReport>({
    queryKey: ["/api/admin/moat/readiness-report", reportGrade],
    queryFn: () => fetchJson<ReadinessReport>(`/api/admin/moat/readiness-report?${new URLSearchParams(reportGrade === "all" ? {} : { grade: reportGrade })}`),
  });

  const { data: schools = [], isLoading: schoolsLoading, error: schoolsError } = useQuery<SchoolRow[]>({
    queryKey: ["/api/admin/moat/schools", legacyGrade],
    queryFn: () => fetchJson<SchoolRow[]>(`/api/admin/moat/schools?${new URLSearchParams(legacyGrade === "all" ? {} : { grade: legacyGrade })}`),
  });

  const parishes = useMemo(
    () => Array.from(new Set(schools.map(s => s.parish).filter((p): p is string => !!p && p.trim() !== ""))).sort(),
    [schools],
  );

  const visibleSchools = useMemo(() => {
    const filtered = parish === "all" ? schools : schools.filter(s => (s.parish || "") === parish);
    return [...filtered].sort((a, b) => {
      const diff = sortKey === "accuracy" ? a.accuracy - b.accuracy : a.students - b.students;
      return sortDir === "asc" ? diff : -diff;
    });
  }, [schools, parish, sortKey, sortDir]);

  const toggleSort = (key: "accuracy" | "students") => {
    if (sortKey === key) setSortDir(d => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("desc"); }
  };

  const downloadReport = () => {
    if (!report) return;
    const rows: unknown[][] = [
      ["EduFocal Academy: State of PEP readiness"],
      ["Generated", report.generatedAt],
      ["Grade filter", reportGrade === "all" ? "All grades" : `Grade ${reportGrade}`],
      [],
      ["Parishes"],
      ["Parish", "Grade", "Live students", "Avg national percentile", "Legacy students", "Legacy accuracy %"],
      ...report.parishes.map(p => [p.parish, p.grade, p.liveStudents, p.avgPercentile ?? "", p.legacyStudents, p.legacyAccuracy ?? ""]),
      [],
      ["Schools"],
      ["School", "Parish", "Grade", "Live students", "With ability estimate", "Avg national percentile", "Legacy students", "Legacy accuracy %"],
      ...report.schools.map(s => [s.school, s.parish ?? "", s.grade, s.students, s.withAbility, s.avgPercentile ?? "", s.legacyStudents ?? "", s.legacyAccuracy ?? ""]),
    ];
    downloadCsv(`pep-readiness-${reportGrade === "all" ? "all-grades" : `grade-${reportGrade}`}.csv`, toCsv(rows));
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2"><School className="w-4 h-4" /> State of PEP readiness</CardTitle>
              <CardDescription>
                Live Academy students grouped by school and parish, with their average position on the national ability scale, next to the legacy
                accuracy benchmark for the same school. Schools with fewer than 3 active students are left out.
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <GradeSelect value={reportGrade} onChange={setReportGrade} />
              <Button variant="outline" onClick={downloadReport} disabled={!report} data-testid="button-download-readiness">
                <Download className="w-4 h-4 mr-2" /> Download CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {reportError ? (
            <ErrorState error={reportError} />
          ) : reportLoading || !report ? (
            <TableSkeleton rows={6} cols={5} />
          ) : report.parishes.length === 0 && report.schools.length === 0 ? (
            <EmptyState title="No readiness data yet" hint="Needs active students with an ability estimate. Press Refresh live data on the Overview tab." />
          ) : (
            <>
              <div>
                <h3 className="text-sm font-semibold mb-2">Parishes</h3>
                {report.parishes.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No parish rows. Parishes come from the legacy school benchmarks, so a school has to match one.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Parish</TableHead>
                          <TableHead>Grade</TableHead>
                          <TableHead className="text-right">Live students</TableHead>
                          <TableHead className="text-right">Avg national percentile</TableHead>
                          <TableHead className="text-right">Legacy students</TableHead>
                          <TableHead className="text-right">Legacy accuracy</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {report.parishes.map((p, i) => (
                          <TableRow key={`${p.parish}-${p.grade}-${i}`} data-testid={`row-parish-${i}`}>
                            <TableCell className="font-medium">{p.parish}</TableCell>
                            <TableCell className="whitespace-nowrap">Grade {p.grade}</TableCell>
                            <TableCell className="text-right font-mono">{p.liveStudents.toLocaleString()}</TableCell>
                            <TableCell className="text-right font-mono">{pct(p.avgPercentile, false)}</TableCell>
                            <TableCell className="text-right font-mono">{p.legacyStudents.toLocaleString()}</TableCell>
                            <TableCell className="text-right font-mono">{pct(p.legacyAccuracy, false)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-sm font-semibold mb-2">Schools</h3>
                {report.schools.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No schools with 3 or more active students for this grade.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="min-w-[220px]">School</TableHead>
                          <TableHead>Parish</TableHead>
                          <TableHead>Grade</TableHead>
                          <TableHead className="text-right">Live students</TableHead>
                          <TableHead className="text-right">With ability</TableHead>
                          <TableHead className="text-right">Avg percentile</TableHead>
                          <TableHead className="text-right">Legacy students</TableHead>
                          <TableHead className="text-right">Legacy accuracy</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {report.schools.map((s, i) => (
                          <TableRow key={`${s.school}-${s.grade}-${i}`} data-testid={`row-readiness-school-${i}`}>
                            <TableCell className="font-medium">{s.school}</TableCell>
                            <TableCell className="text-muted-foreground">{s.parish || "n/a"}</TableCell>
                            <TableCell className="whitespace-nowrap">Grade {s.grade}</TableCell>
                            <TableCell className="text-right font-mono">{s.students.toLocaleString()}</TableCell>
                            <TableCell className="text-right font-mono">{s.withAbility.toLocaleString()}</TableCell>
                            <TableCell className="text-right font-mono">{pct(s.avgPercentile, false)}</TableCell>
                            <TableCell className="text-right font-mono">{s.legacyStudents === null ? "n/a" : s.legacyStudents.toLocaleString()}</TableCell>
                            <TableCell className="text-right font-mono">{pct(s.legacyAccuracy, false)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">Generated {new Date(report.generatedAt).toLocaleString()}.</p>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Legacy benchmarks</CardTitle>
          <CardDescription>
            Aggregate accuracy per school from the legacy archive of Jamaican student answers. Aggregates only. No individual student is identifiable here.
          </CardDescription>
          <div className="flex flex-wrap gap-3 pt-2">
            <GradeSelect value={legacyGrade} onChange={setLegacyGrade} />
            <Select value={parish} onValueChange={setParish}>
              <SelectTrigger className="w-[200px]"><SelectValue placeholder="Parish" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All parishes</SelectItem>
                {parishes.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="ml-auto text-sm text-muted-foreground self-center">{visibleSchools.length} school row{visibleSchools.length === 1 ? "" : "s"}</div>
          </div>
        </CardHeader>
        <CardContent>
          {schoolsError ? (
            <ErrorState error={schoolsError} />
          ) : schoolsLoading ? (
            <TableSkeleton rows={8} cols={5} />
          ) : visibleSchools.length === 0 ? (
            <EmptyState title="No school benchmarks yet" hint="Run the legacy import. School benchmarks are built from the archived answers." />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[240px]">School</TableHead>
                    <TableHead>Parish</TableHead>
                    <TableHead>Grade</TableHead>
                    <TableHead className="text-right">
                      <button className="inline-flex items-center gap-1 hover:text-foreground" onClick={() => toggleSort("students")} data-testid="sort-students">
                        Students <ArrowUpDown className="w-3 h-3" />
                      </button>
                    </TableHead>
                    <TableHead className="text-right">Answers</TableHead>
                    <TableHead className="text-right">
                      <button className="inline-flex items-center gap-1 hover:text-foreground" onClick={() => toggleSort("accuracy")} data-testid="sort-accuracy">
                        Accuracy <ArrowUpDown className="w-3 h-3" />
                      </button>
                    </TableHead>
                    <TableHead>Source</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleSchools.map((s, i) => (
                    <TableRow key={`${s.schoolName}-${s.grade}-${s.source}-${i}`} data-testid={`row-school-${i}`}>
                      <TableCell className="font-medium">{s.schoolName}</TableCell>
                      <TableCell className="text-muted-foreground">{s.parish || "n/a"}</TableCell>
                      <TableCell className="whitespace-nowrap">Grade {s.grade}</TableCell>
                      <TableCell className="text-right font-mono">{s.students.toLocaleString()}</TableCell>
                      <TableCell className="text-right font-mono">{s.answers.toLocaleString()}</TableCell>
                      <TableCell className="text-right font-mono">{pct(s.accuracy)}</TableCell>
                      <TableCell><Badge variant="outline">{s.source}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------------
// 7. PEP outcomes
// ---------------------------------------------------------------------------------

const CSV_HEADER =
  "email,examYear,examGrade,placementSchool,mathematics_level,mathematics_score,languageArts_level,languageArts_score,science_level,science_score,socialStudies_level,socialStudies_score,abilityTest_level,abilityTest_score,overall_level,overall_score,notes";

type SubjectFormState = Record<SubjectKey, { level: string; score: string }>;

function emptySubjectForm(): SubjectFormState {
  return SUBJECT_KEYS.reduce((acc, s) => {
    acc[s.key] = { level: "none", score: "" };
    return acc;
  }, {} as SubjectFormState);
}

function buildSubjectsPayload(form: SubjectFormState): Record<string, SubjectResult> {
  const subjects: Record<string, SubjectResult> = {};
  for (const { key } of SUBJECT_KEYS) {
    const { level, score } = form[key];
    const hasLevel = level && level !== "none";
    const hasScore = score.trim() !== "" && !Number.isNaN(Number(score));
    if (hasLevel || hasScore) {
      subjects[key] = { level: hasLevel ? level : null, score: hasScore ? Number(score) : null };
    }
  }
  return subjects;
}

function OutcomesTab() {
  const { toast } = useToast();
  const currentYear = new Date().getFullYear();

  const { data, isLoading, error } = useQuery<OutcomesResponse>({ queryKey: ["/api/admin/moat/outcomes"] });

  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Outcome | null>(null);
  const [form, setForm] = useState({ email: "", userId: "", examYear: String(currentYear), examGrade: "6", placementSchool: "", notes: "" });
  const [subjectForm, setSubjectForm] = useState<SubjectFormState>(emptySubjectForm());
  const [formError, setFormError] = useState<string | null>(null);
  const [csvText, setCsvText] = useState("");
  const [csvError, setCsvError] = useState<string | null>(null);
  const [csvReport, setCsvReport] = useState<{ imported: number; failed: number; errors: string[] } | null>(null);

  const [campaignYear, setCampaignYear] = useState(String(currentYear));
  const [campaignRequestedYear, setCampaignRequestedYear] = useState<string | null>(null);

  const invalidateOutcomes = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/admin/moat/outcomes"] });
    queryClient.invalidateQueries({ queryKey: ["/api/admin/moat/summary"] });
  };

  const saveMutation = useMutation({
    mutationFn: async (body: any) => (await apiRequest("POST", "/api/admin/moat/outcomes", body)).json(),
    onSuccess: (res: { imported: number; failed: number; results: Array<{ index: number; ok: boolean; error?: string }> }) => {
      invalidateOutcomes();
      const errors = (res.results || []).filter(r => !r.ok).map(r => `Row ${r.index + 1}: ${r.error || "failed"}`);
      if (res.failed > 0 && res.imported === 0) {
        setFormError(errors.join("\n"));
        toast({ title: "Nothing saved", description: errors[0] || "The row was rejected.", variant: "destructive" });
        return;
      }
      setFormError(null);
      setAddOpen(false);
      setForm({ email: "", userId: "", examYear: String(currentYear), examGrade: "6", placementSchool: "", notes: "" });
      setSubjectForm(emptySubjectForm());
      toast({
        title: "Result saved",
        description: res.failed > 0 ? `${res.imported} saved, ${res.failed} rejected. ${errors[0] || ""}` : "PEP result recorded and the predictor was refitted.",
        variant: res.failed > 0 ? "destructive" : "default",
      });
    },
    onError: (e: any) => {
      setFormError(e?.message || "Unknown error");
      toast({ title: "Could not save the result", description: e?.message || "Unknown error", variant: "destructive" });
    },
  });

  const importMutation = useMutation({
    mutationFn: async (rows: any[]) => (await apiRequest("POST", "/api/admin/moat/outcomes", { rows })).json(),
    onSuccess: (res: { imported: number; failed: number; results: Array<{ index: number; ok: boolean; error?: string }> }) => {
      invalidateOutcomes();
      const errors = (res.results || []).filter(r => !r.ok).map(r => `Row ${r.index + 2}: ${r.error || "failed"}`);
      setCsvReport({ imported: res.imported, failed: res.failed, errors });
      toast({
        title: `Imported ${res.imported} result${res.imported === 1 ? "" : "s"}`,
        description: res.failed > 0 ? `${res.failed} row${res.failed === 1 ? "" : "s"} rejected. See the list in the dialog.` : "All rows accepted.",
        variant: res.failed > 0 ? "destructive" : "default",
      });
    },
    onError: (e: any) => {
      setCsvError(e?.message || "Unknown error");
      toast({ title: "Import failed", description: e?.message || "Unknown error", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/admin/moat/outcomes/${id}`),
    onSuccess: () => {
      invalidateOutcomes();
      setDeleteTarget(null);
      toast({ title: "Result deleted" });
    },
    onError: (e: any) => toast({ title: "Delete failed", description: e?.message || "Unknown error", variant: "destructive" }),
  });

  const { data: campaign, isLoading: campaignLoading, error: campaignError } = useQuery<{ examYear: number; students: CampaignStudent[] }>({
    queryKey: ["/api/admin/moat/outcomes/campaign", campaignRequestedYear],
    queryFn: () => fetchJson<{ examYear: number; students: CampaignStudent[] }>(`/api/admin/moat/outcomes/campaign?examYear=${campaignRequestedYear}`),
    enabled: !!campaignRequestedYear,
  });

  const submitAdd = () => {
    setFormError(null);
    const subjects = buildSubjectsPayload(subjectForm);
    if (!form.email.trim() && !form.userId.trim()) {
      setFormError("Give a student email or a user id so the result can be attached to an account.");
      return;
    }
    if (Object.keys(subjects).length === 0) {
      setFormError("Set at least one subject level or score.");
      return;
    }
    saveMutation.mutate({
      ...(form.email.trim() ? { email: form.email.trim() } : {}),
      ...(form.userId.trim() ? { userId: form.userId.trim() } : {}),
      examYear: Number(form.examYear),
      examGrade: Number(form.examGrade),
      placementSchool: form.placementSchool.trim() || null,
      subjects,
      notes: form.notes.trim() || null,
    });
  };

  const submitCsv = () => {
    setCsvError(null);
    setCsvReport(null);
    const parsed = parseCsv(csvText);
    if (parsed.length < 2) {
      setCsvError("Paste a header row plus at least one data row.");
      return;
    }
    const header = parsed[0].map(h => h.trim());
    const idx = (name: string) => header.indexOf(name);
    if (idx("examYear") === -1) {
      setCsvError(`Header must include examYear. Expected header:\n${CSV_HEADER}`);
      return;
    }
    const at = (row: string[], name: string) => {
      const i = idx(name);
      return i === -1 ? "" : (row[i] ?? "").trim();
    };
    const rows = parsed.slice(1).map(row => {
      const subjects: Record<string, SubjectResult> = {};
      for (const { key } of SUBJECT_KEYS) {
        const level = at(row, `${key}_level`);
        const score = at(row, `${key}_score`);
        if (level || score) {
          subjects[key] = { level: level || null, score: score === "" ? null : Number(score) };
        }
      }
      const body: Record<string, unknown> = { examYear: Number(at(row, "examYear")), subjects };
      const email = at(row, "email");
      const legacyId = at(row, "legacyId");
      const examGrade = at(row, "examGrade");
      const placementSchool = at(row, "placementSchool");
      const notes = at(row, "notes");
      if (email) body.email = email;
      if (legacyId) body.legacyId = Number(legacyId);
      body.examGrade = examGrade ? Number(examGrade) : 6;
      if (placementSchool) body.placementSchool = placementSchool;
      if (notes) body.notes = notes;
      return body;
    });
    if (rows.length === 0) {
      setCsvError("No data rows found.");
      return;
    }
    importMutation.mutate(rows);
  };

  const downloadCampaign = () => {
    if (!campaign) return;
    const rows: unknown[][] = [
      ["First name", "Last name", "Email", "Parent email", "Parent phone", "School", "Grade", "Last active", "User id"],
      ...campaign.students.map(s => [s.firstName ?? "", s.lastName ?? "", s.email ?? "", s.parentEmail ?? "", s.parentPhone ?? "", s.school ?? "", s.grade, s.lastActiveAt ?? "", s.userId]),
    ];
    downloadCsv(`pep-outcomes-campaign-${campaign.examYear}.csv`, toCsv(rows));
  };

  const outcomes = data?.outcomes ?? [];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2"><ClipboardList className="w-4 h-4" /> Real PEP results</CardTitle>
              <CardDescription>
                The ground truth the readiness predictor is scored against. Thirty or more results with a readiness score unlock calibration, which turns
                a readiness percentage into a real probability of reaching Proficient.
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setCsvError(null); setCsvReport(null); setImportOpen(true); }} data-testid="button-import-outcomes">
                <Upload className="w-4 h-4 mr-2" /> Import CSV
              </Button>
              <Button onClick={() => { setFormError(null); setAddOpen(true); }} data-testid="button-add-outcome">
                <Plus className="w-4 h-4 mr-2" /> Add result
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {error ? (
            <ErrorState error={error} />
          ) : isLoading ? (
            <TableSkeleton rows={6} cols={6} />
          ) : outcomes.length === 0 ? (
            <EmptyState title="No real PEP results on file yet" hint="Add one by hand, or paste a CSV export from a school or a parent survey." />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[180px]">Student</TableHead>
                    <TableHead>Year</TableHead>
                    <TableHead>Grade</TableHead>
                    <TableHead>Placement school</TableHead>
                    <TableHead className="min-w-[280px]">Subjects</TableHead>
                    <TableHead>Reported by</TableHead>
                    <TableHead>Added</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {outcomes.map(o => (
                    <TableRow key={o.id} data-testid={`row-outcome-${o.id}`}>
                      <TableCell>
                        {o.student ? (
                          <div>
                            <p className="font-medium">{o.student.firstName} {o.student.lastName}</p>
                            <p className="text-xs text-muted-foreground">{o.student.school || "no school on file"}</p>
                          </div>
                        ) : o.legacyId ? (
                          <span className="font-mono text-sm">legacy #{o.legacyId}</span>
                        ) : (
                          <span className="text-muted-foreground text-sm">Unlinked</span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono">{o.examYear}</TableCell>
                      <TableCell className="whitespace-nowrap">Grade {o.examGrade}</TableCell>
                      <TableCell className="text-sm">{o.placementSchool || <span className="text-muted-foreground">n/a</span>}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {SUBJECT_KEYS.filter(s => o.subjects?.[s.key]).map(s => {
                            const r = o.subjects[s.key]!;
                            return (
                              <Badge key={s.key} className={`border-0 ${r.level ? LEVEL_BADGE[r.level] || "bg-muted text-muted-foreground" : "bg-muted text-muted-foreground"}`}>
                                {s.label}: {r.level || "n/a"}{typeof r.score === "number" ? ` (${r.score})` : ""}
                              </Badge>
                            );
                          })}
                          {SUBJECT_KEYS.every(s => !o.subjects?.[s.key]) && <span className="text-muted-foreground text-sm">n/a</span>}
                        </div>
                        {o.notes && <p className="text-xs text-muted-foreground mt-1">{o.notes}</p>}
                      </TableCell>
                      <TableCell><Badge variant="outline">{o.reportedBy}</Badge></TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{formatDate(o.createdAt)}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" className="text-destructive" title="Delete result" onClick={() => setDeleteTarget(o)} data-testid={`button-delete-outcome-${o.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Campaign list */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4" /> Campaign list</CardTitle>
          <CardDescription>
            Grade 6 (and just-promoted Grade 7) accounts with no result on file for the exam year. These are the students worth asking. This builds the contact
            list only. Nothing is sent from this page: download the CSV and run the outreach wherever you normally would.
          </CardDescription>
          <div className="flex flex-wrap gap-2 pt-2">
            <Input
              type="number"
              className="w-[140px]"
              value={campaignYear}
              onChange={e => setCampaignYear(e.target.value)}
              placeholder="Exam year"
              data-testid="input-campaign-year"
            />
            <Button variant="outline" onClick={() => setCampaignRequestedYear(campaignYear)} disabled={!campaignYear} data-testid="button-build-campaign">
              Build list
            </Button>
            <Button variant="outline" onClick={downloadCampaign} disabled={!campaign || campaign.students.length === 0} data-testid="button-download-campaign">
              <Download className="w-4 h-4 mr-2" /> Download CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!campaignRequestedYear ? (
            <EmptyState title="No list built yet" hint="Pick an exam year and press Build list." />
          ) : campaignError ? (
            <ErrorState error={campaignError} />
          ) : campaignLoading ? (
            <TableSkeleton rows={5} cols={5} />
          ) : !campaign || campaign.students.length === 0 ? (
            <EmptyState title="Nobody to chase" hint={`Every eligible student already has a result on file for ${campaignRequestedYear}.`} />
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-3">{campaign.students.length.toLocaleString()} student{campaign.students.length === 1 ? "" : "s"} for {campaign.examYear}.</p>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Student</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Parent email</TableHead>
                      <TableHead>Parent phone</TableHead>
                      <TableHead>School</TableHead>
                      <TableHead>Grade</TableHead>
                      <TableHead>Last active</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {campaign.students.map(s => (
                      <TableRow key={s.userId} data-testid={`row-campaign-${s.userId}`}>
                        <TableCell className="font-medium">{[s.firstName, s.lastName].filter(Boolean).join(" ") || "n/a"}</TableCell>
                        <TableCell className="text-sm">{s.email || "n/a"}</TableCell>
                        <TableCell className="text-sm">{s.parentEmail || "n/a"}</TableCell>
                        <TableCell className="text-sm">{s.parentPhone || "n/a"}</TableCell>
                        <TableCell className="text-sm">{s.school || "n/a"}</TableCell>
                        <TableCell className="whitespace-nowrap">Grade {s.grade}</TableCell>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{formatDate(s.lastActiveAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Add result dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5" /> Add a real PEP result</DialogTitle>
            <DialogDescription>
              Attach the result to an existing account by email or user id. Saving refits the predictor automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="outcome-email">Student email</Label>
                <Input id="outcome-email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="student@example.com" data-testid="input-outcome-email" />
              </div>
              <div>
                <Label htmlFor="outcome-userid">…or user id</Label>
                <Input id="outcome-userid" value={form.userId} onChange={e => setForm(f => ({ ...f, userId: e.target.value }))} placeholder="uuid" data-testid="input-outcome-userid" />
              </div>
              <div>
                <Label htmlFor="outcome-year">Exam year</Label>
                <Input id="outcome-year" type="number" value={form.examYear} onChange={e => setForm(f => ({ ...f, examYear: e.target.value }))} data-testid="input-outcome-year" />
              </div>
              <div>
                <Label>Exam grade</Label>
                <Select value={form.examGrade} onValueChange={v => setForm(f => ({ ...f, examGrade: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="4">Grade 4 (Literacy / Numeracy)</SelectItem>
                    <SelectItem value="5">Grade 5 (Performance Task)</SelectItem>
                    <SelectItem value="6">Grade 6 (PEP exit)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="outcome-school">Placement school</Label>
                <Input id="outcome-school" value={form.placementSchool} onChange={e => setForm(f => ({ ...f, placementSchool: e.target.value }))} placeholder="e.g. Campion College" data-testid="input-outcome-school" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Subject results</Label>
              <div className="space-y-2">
                {SUBJECT_KEYS.map(s => (
                  <div key={s.key} className="grid grid-cols-[1fr_auto_110px] gap-2 items-center">
                    <span className="text-sm">{s.label}</span>
                    <Select value={subjectForm[s.key].level} onValueChange={v => setSubjectForm(sf => ({ ...sf, [s.key]: { ...sf[s.key], level: v } }))}>
                      <SelectTrigger className="w-[190px]"><SelectValue placeholder="Level" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No level</SelectItem>
                        {PEP_LEVELS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      placeholder="Score"
                      value={subjectForm[s.key].score}
                      onChange={e => setSubjectForm(sf => ({ ...sf, [s.key]: { ...sf[s.key], score: e.target.value } }))}
                      data-testid={`input-score-${s.key}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="outcome-notes">Notes</Label>
              <Textarea id="outcome-notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional, max 500 characters" />
            </div>

            {formError && <pre className="text-xs text-destructive whitespace-pre-wrap bg-destructive/10 rounded-md p-3 max-h-32 overflow-auto">{formError}</pre>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={submitAdd} disabled={saveMutation.isPending} data-testid="button-save-outcome">
              {saveMutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : "Save result"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import CSV dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Upload className="w-5 h-5" /> Import PEP results from CSV</DialogTitle>
            <DialogDescription>
              Paste rows with this header. Blank cells are skipped. Levels must be one of {PEP_LEVELS.join(", ")}; scores are 0-100.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <pre className="text-[10px] bg-muted rounded-md p-2 overflow-x-auto flex-1">{CSV_HEADER}</pre>
              <Button size="sm" variant="outline" onClick={() => setCsvText(t => (t.trim() ? t : `${CSV_HEADER}\n`))}>Use header</Button>
            </div>
            <Textarea
              value={csvText}
              onChange={e => setCsvText(e.target.value)}
              className="font-mono text-xs h-[40vh]"
              spellCheck={false}
              placeholder={`${CSV_HEADER}\nchild@example.com,${currentYear},6,Campion College,Proficient,78,Proficient,81,Developing,64,Proficient,72,,,Proficient,74,`}
              data-testid="textarea-csv"
            />
            {csvError && <pre className="text-xs text-destructive whitespace-pre-wrap bg-destructive/10 rounded-md p-3 max-h-32 overflow-auto">{csvError}</pre>}
            {csvReport && (
              <div className="text-sm space-y-2">
                <p>
                  <span className="font-semibold text-emerald-700 dark:text-emerald-400">{csvReport.imported} imported</span>
                  {csvReport.failed > 0 && <span className="font-semibold text-destructive"> · {csvReport.failed} failed</span>}
                </p>
                {csvReport.errors.length > 0 && (
                  <pre className="text-xs text-destructive whitespace-pre-wrap bg-destructive/10 rounded-md p-3 max-h-40 overflow-auto">{csvReport.errors.join("\n")}</pre>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Close</Button>
            <Button onClick={submitCsv} disabled={importMutation.isPending || !csvText.trim()} data-testid="button-submit-csv">
              {importMutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing…</> : "Parse and import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={o => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this PEP result?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.student
                ? `${deleteTarget.student.firstName} ${deleteTarget.student.lastName}, ${deleteTarget.examYear} Grade ${deleteTarget.examGrade}.`
                : `${deleteTarget?.examYear ?? ""} Grade ${deleteTarget?.examGrade ?? ""}.`}{" "}
              The predictor is refitted without it. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              data-testid="button-confirm-delete-outcome"
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------------

export default function AdminMoat() {
  const [tab, setTab] = useState("overview");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <BrainCircuit className="w-6 h-6 text-primary" /> Data Moat
        </h1>
        <p className="text-muted-foreground text-sm max-w-3xl">
          What 44 million answers from Jamaican students turned into: calibrated questions, the specific wrong ideas children hold, which topics unlock
          which, how fast each topic fades, school and parish benchmarks, and the real PEP results the readiness predictor is scored against.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <TabsList className="flex flex-wrap h-auto justify-start gap-1">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="items" data-testid="tab-items">Item quality</TabsTrigger>
          <TabsTrigger value="misconceptions" data-testid="tab-misconceptions">Misconceptions</TabsTrigger>
          <TabsTrigger value="prerequisites" data-testid="tab-prerequisites">Prerequisites</TabsTrigger>
          <TabsTrigger value="retention" data-testid="tab-retention">Retention</TabsTrigger>
          <TabsTrigger value="schools" data-testid="tab-schools">Schools &amp; parishes</TabsTrigger>
          <TabsTrigger value="outcomes" data-testid="tab-outcomes">PEP outcomes</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6 mt-0"><OverviewTab /></TabsContent>
        <TabsContent value="items" className="space-y-6 mt-0"><ItemQualityTab /></TabsContent>
        <TabsContent value="misconceptions" className="space-y-6 mt-0"><MisconceptionsTab /></TabsContent>
        <TabsContent value="prerequisites" className="space-y-6 mt-0"><PrerequisitesTab /></TabsContent>
        <TabsContent value="retention" className="space-y-6 mt-0"><RetentionTab /></TabsContent>
        <TabsContent value="schools" className="space-y-6 mt-0"><SchoolsTab /></TabsContent>
        <TabsContent value="outcomes" className="space-y-6 mt-0"><OutcomesTab /></TabsContent>
      </Tabs>
    </div>
  );
}
